import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ExportService } from '@helpers/api/export.service';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisConfigService
} from '@campaign-portal/components-library';
import { RulesFieldsService } from './rules.fields.service';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import {
	RulesDeleteRequest,
	RulesDeleteResponse,
	RulesReadRequest,
	RulesReadResponse,
	RulesUpdateRequest,
	RulesUpdateResponse
} from '@helpers/types/hlr';
import { ChoiceHLR, ChoiceLocal, Rule } from '@helpers/types/models';
import { map } from 'rxjs/operators';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { eapiEnable } from '../../app.module';

@Injectable({
	providedIn: 'root'
})
export class RulesService implements AbstractCRUDService {
	readonly entity = '';
	readonly title = '';
	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly config: AlarisConfigService,
		private readonly fields: RulesFieldsService,
		private readonly exportService: ExportService,
	) {}

	read(params: ReadRequest): Observable<ReadResponse<Rule[]>> {
		const parsedParams: RulesReadRequest = this.prepareParams(params);
		return this.api.loader<RulesReadResponse>('Rules.Read', parsedParams, this.loading$).pipe(
			map((resp) => {
				return {
					Success: !!resp.data,
					Total: resp.length,
					Data: resp.data.map((el) => {
						el.choiceList = this.toLocalChoiceFormat(el.choiceList as ChoiceHLR[]);
						return el;
					})
				};
			})
		);
	}

	update(params: RulesUpdateRequest): Observable<RulesUpdateResponse> {
		if (!eapiEnable) {
			if (params.productList) {
				params.productList = params.productList.toString().split(',');
			} else {
				params.productList = [];
			}
			if (params.productsNames) {
				params.productsNames =
					params.productsNames.length > 0 ? params.productsNames.toString().split(',') : [];
			} else {
				params.productsNames = [];
			}
		}
		params.choiceList = this.toHlrChoiceFormat(params.choiceList as ChoiceLocal[]);
		return this.api.loader<RulesUpdateResponse>(
			params.ruleId ? 'Rules.Update' : 'Rules.Create',
			params,
			this.loading$
		);
	}

	toHlrChoiceFormat(choices: ChoiceLocal[]): ChoiceHLR[] {
		return choices.map((choiceLocal) => {
			const sources: Record<string, number>[] = [];
			choiceLocal.sources.forEach(({ Source, Share }) => {
				if (Source) {
					Source.forEach((sourceName) => {
						sources.push({ [sourceName]: Share / 100 });
					});
				}
			});
			return { sources, enableLCR: choiceLocal.enableLCR === 'Yes' };
		});
	}

	toLocalChoiceFormat(choices: ChoiceHLR[]): ChoiceLocal[] {
		return choices.map((choiceHLR) => {
			const sourcesGrouped: { [share: number]: string[] } = {};
			const otherSources: { Source: string[]; Share: number }[] = [];

			choiceHLR.sources.forEach((sourceObj) => {
				const [sourceName, shareValue] = Object.entries(sourceObj)[0];
				const shareNum = shareValue;

				if (shareNum === 1) {
					if (!sourcesGrouped[shareNum]) {
						sourcesGrouped[shareNum] = [];
					}
					sourcesGrouped[shareNum].push(sourceName);
				} else {
					otherSources.push({ Source: [sourceName], Share: shareNum * 100 });
				}
			});

			const sourcesResult: { Source: string[]; Share: number }[] = [];

			const groupings = Object.entries(sourcesGrouped);
			groupings.forEach(([shareStr, sourceNames]) => {
				sourcesResult.push({ Source: sourceNames, Share: Number(shareStr) * 100 });
			});
			sourcesResult.push(...otherSources);
			return {
				sources: sourcesResult,
				enableLCR: choiceHLR.enableLCR ? 'Yes' : 'No'
			};
		});
	}

	delete(params: RulesDeleteRequest): Observable<RulesDeleteResponse> {
		return this.api.loader<RulesDeleteResponse>('Rules.Delete', params, this.loading$);
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		params = {
			...params,
			Paging: { Skip: 0, Take: total }
		};
		return this.exportService.export(
			this.config.api,
			'Rules.Read',
			this.prepareParams(params),
			this.fields.headers,
			'rules'
		);
	}

	private prepareParams(params: RPCRequestParams): RulesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'rule_name',
			direction: 'asc',
			filter: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				}) : []
		};
	}

	private parseFilterField(filter: Filter): { key: string; value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		const productsNames: string[] = [];
		const productValues: string[] = [];
		switch (variable) {
			case 'ruleId':
				return { key: 'id', value: value + '' };
			case 'choiceListName':
				return { key: 'choice_list_name', value: value + '' };
			case 'prefixList':
				return { key: 'prefix_detailed_list', value: value + '' };
			case 'productsType':
				return { key: 'products_type', value: value + '' };
			case 'productsNames':
				if (eapiEnable) {
					(value as string[]).forEach((val) => {
						productsNames.push(val);
					});
					return { key: 'products_names', value: productsNames + '' };
				} else {
					return { key: 'products_names', value: value + '' };
				}
			case 'productList':
				if (eapiEnable) {
					(value as { name: string; value: number }[]).forEach((val) => {
						productValues.push(val.value.toString());
					});
					return { key: 'product_list', value: productValues + '' };
				} else {
					return { key: 'product_list', value: value + '' };
				}
			default:
				return { key: variable, value: value + '' };
		}
	}
}
